/**
 * UniversalTable Style Utilities
 *
 * Reusable utility functions for generating consistent class names
 * based on component state and configuration.
 */

import { TABLE_DESIGN } from "../constants/tableDesign";

/**
 * Get visibility class based on responsive breakpoint configuration
 *
 * @param visible - Breakpoint at which to show the column
 * @returns Tailwind class string for visibility
 *
 * @example
 * ```tsx
 * <div className={getColumnVisibilityClass('lg')}>
 *   // Hidden on mobile, visible on lg+
 * </div>
 * ```
 */
export const getColumnVisibilityClass = (
  visible?: "lg" | "xl",
  variant: "flex" | "block" = "flex",
): string => {
  if (!visible) return "";

  if (variant === "block") {
    return visible === "lg"
      ? TABLE_DESIGN.responsive.hiddenLgBlock
      : TABLE_DESIGN.responsive.hiddenXlBlock;
  }

  return visible === "lg"
    ? TABLE_DESIGN.responsive.hiddenLg
    : TABLE_DESIGN.responsive.hiddenXl;
};

/**
 * Get row state classes based on selection and highlighting
 *
 * @param isSelected - Whether the row is selected
 * @param isHighlighted - Whether the row is highlighted (e.g., after creation)
 * @returns Combined class string
 *
 * @example
 * ```tsx
 * <div className={getRowStateClass(true, false)}>
 *   // Selected row styling
 * </div>
 * ```
 */
export const getRowStateClass = (
  isSelected: boolean = false,
  isHighlighted: boolean = false,
): string => {
  const classes: string[] = [];

  if (isSelected) {
    classes.push(TABLE_DESIGN.colors.selected);
  }

  if (isHighlighted) {
    classes.push(TABLE_DESIGN.colors.highlighted);
  }

  return classes.join(" ");
};

/**
 * Get cell alignment class
 *
 * @param align - Alignment direction
 * @returns Tailwind flex alignment class
 *
 * @example
 * ```tsx
 * <div className={getCellAlignmentClass('center')}>
 *   // Centered content
 * </div>
 * ```
 */
export const getCellAlignmentClass = (
  align?: "left" | "center" | "right",
): string => {
  switch (align) {
    case "center":
      return "justify-center";
    case "right":
      return "justify-end";
    case "left":
    default:
      return "justify-start";
  }
};

/**
 * Get complete row container classes
 *
 * @param isSelected - Whether the row is selected
 * @param isHighlighted - Whether the row is highlighted
 * @returns Complete class string for row container
 */
export const getRowContainerClass = (
  isSelected: boolean = false,
  isHighlighted: boolean = false,
): string => {
  const baseClasses = [
    "grid",
    TABLE_DESIGN.layout.gridGap,
    TABLE_DESIGN.spacing.row,
    "cursor-pointer",
    TABLE_DESIGN.animations.transition,
    TABLE_DESIGN.colors.rowBorder,
    TABLE_DESIGN.colors.rowBorderHover,
    TABLE_DESIGN.animations.hoverTransform,
    TABLE_DESIGN.typography.rowText,
    "relative z-0",
    TABLE_DESIGN.colors.background.primary,
    TABLE_DESIGN.colors.hover,
  ];

  const stateClasses = getRowStateClass(isSelected, isHighlighted);

  return [...baseClasses, stateClasses].filter(Boolean).join(" ");
};

/**
 * Get cell container classes
 *
 * @param colSpan - Number of columns to span
 * @param hidden - Whether cell is hidden on small screens
 * @param align - Cell content alignment
 * @param className - Additional custom classes
 * @returns Complete class string for cell
 */
export const getCellContainerClass = (
  colSpan: number = 1,
  hidden: boolean = false,
  align?: "left" | "center" | "right",
  className?: string,
): string => {
  const baseClasses = [
    `col-span-${colSpan}`,
    hidden ? TABLE_DESIGN.responsive.hiddenLg : "flex",
    "items-center",
    getCellAlignmentClass(align),
    "relative",
    TABLE_DESIGN.colors.cellBorder,
    "overflow-hidden",
  ];

  if (className) {
    baseClasses.push(className);
  }

  return baseClasses.join(" ");
};

/**
 * Get header container classes
 *
 * @returns Complete class string for table header
 */
export const getHeaderContainerClass = (): string => {
  return ["flex items-center justify-between", "mb-6"].join(" ");
};

/**
 * Get column header container classes
 *
 * @returns Complete class string for column headers
 */
export const getColumnHeaderContainerClass = (): string => {
  return [
    "sticky z-30",
    TABLE_DESIGN.colors.columnHeaderBg,
    TABLE_DESIGN.colors.border,
    "border-b",
    TABLE_DESIGN.shadows.header,
    "backdrop-blur-xs",
  ].join(" ");
};

/**
 * Get column header grid classes
 *
 * @returns Complete class string for column header grid
 */
export const getColumnHeaderGridClass = (): string => {
  return [
    "relative grid",
    TABLE_DESIGN.layout.gridGap,
    TABLE_DESIGN.spacing.row,
    TABLE_DESIGN.typography.columnHeader,
  ].join(" ");
};

/**
 * Get badge classes
 *
 * @returns Complete class string for count badge
 */
export const getBadgeClass = (): string => {
  return [
    "bg-moss-primary",
    "text-white",
    "px-4 py-1.5",
    TABLE_DESIGN.borderRadius.badge,
    TABLE_DESIGN.typography.badge,
  ].join(" ");
};

/**
 * Get empty state container classes
 *
 * @returns Complete class string for empty state
 */
export const getEmptyStateClass = (): string => {
  return ["text-center py-16 px-6", TABLE_DESIGN.colors.emptyText].join(" ");
};

/**
 * Get drag indicator classes
 *
 * @returns Complete class string for drag indicator
 */
export const getDragIndicatorClass = (): string => {
  return [
    "pointer-events-none",
    "absolute top-0 bottom-0",
    "w-0.5",
    "bg-moss-primary",
    TABLE_DESIGN.shadows.dragIndicator,
    "z-50",
    "hidden",
    "transition-none",
  ].join(" ");
};

/**
 * Get resize handle classes
 *
 * @returns Complete class string for resize handle
 *
 * Improved for better grab-ability:
 * - Wider hit area (w-6 = 24px instead of w-3 = 12px)
 * - Visible hover state
 * - Touch-friendly sizing
 */
export const getResizeHandleClass = (): string => {
  return [
    "absolute top-0 -right-3",
    "h-full w-6",
    TABLE_DESIGN.animations.resizeCursor,
    "z-30 group",
    "hover:bg-moss-primary/5",
    "transition-colors duration-150",
    "flex items-center justify-center",
  ].join(" ");
};

/**
 * Get resize handle inner line classes
 *
 * @returns Complete class string for resize handle line
 *
 * Enhanced visibility:
 * - Always visible with reasonable opacity
 * - Thicker and more prominent on hover
 * - Smooth transitions
 */
export const getResizeHandleLineClass = (): string => {
  return [
    "absolute inset-y-0 left-1/2 -translate-x-1/2",
    "w-0.5",
    "bg-neutral-primary/20",
    "group-hover:bg-moss-primary/60",
    "group-hover:w-1",
    "group-hover:shadow-sm",
    "transition-all duration-150",
    "rounded-full",
  ].join(" ");
};

/**
 * Get table container classes
 *
 * @returns Complete class string for main table container
 */
export const getTableContainerClass = (): string => {
  return ["w-full"].join(" ");
};

/**
 * Get rows container classes
 *
 * @returns Complete class string for rows container
 */
export const getRowsContainerClass = (): string => {
  return [
    TABLE_DESIGN.colors.separator,
    "relative will-change-[grid-template-columns]",
  ].join(" ");
};
